import { useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Alert,
  Badge,
  BarChart,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Sparkline,
  Spinner,
  StatTile,
} from "@empite/ui-components";
import type { BadgeTone } from "@empite/ui-components";
import { isApiError } from "@empite/api-client";
import type {
  CustomerSubscription,
  DeviceApproval,
  DevicePublicKey,
  Operator,
  SecurityEvent,
  SecurityEventFilter,
  SecurityEventOutcome,
  UserAppSession,
  UserBinding,
  UserDetail as UserDetailData,
  UserDevice,
  UserDeveloperFlagResult,
  UserEntitlement,
} from "../admin-client";
import { revocationSummary } from "../admin-client";
import {
  useAnalyticsUserDetail,
  useReactivateUser,
  useRevokeBinding,
  useRestoreDevice,
  useRevokeDevice,
  useRevokeSession,
  useSetUserDeveloperFlag,
  useSignOutDevice,
  useSuspendUser,
  useUser,
  useUserSecurityEvents,
  useUserSubscriptions,
} from "../admin-hooks";
import { ErrorAlert, RequireOperator, can, errorMessage } from "./guards";
import { ReasonButton } from "./reason-prompt";
import { CopyText, ScopeList, ShortID, StatusBadge, toRFC3339, when, whenExact } from "./common";
import { IDENTITY, READ, WITHHELD, WindowNotices, WindowPicker, money } from "./analytics-common";
import { formatRate, windowMeasured } from "./analytics-window";
import type { AnalyticsSearch } from "./analytics-window";
import {
  allZero,
  applicationParts,
  hasUsage,
  modelParts,
  userRequestSeries,
} from "./analytics-user-usage";
import { DEVICES_READ, DeviceAppMetadata } from "./device-metadata-section";

/** The permissions this page's controls read — these three, plus `analytics:read`
 *  for the Usage card and DEVICES_READ, imported above, for the app-metadata
 *  section on each installation. All are re-checked by the Admin API on every
 *  route; the strings are here to decide what to draw. */
const SUSPEND = "users:suspend";
const REVOKE = "devices:revoke";
/** Its own permission, not SUSPEND: suspending takes access away and this
 *  grants it, so the role that does one need not be the role that does the
 *  other. See PermUsersDeveloperFlag in internal/admin/rbac.go. */
const DEVELOPER_FLAG = "users:developer_flag";

/**
 * One account: who they are, what is installed, what is signed in.
 *
 * This is the page an operator opens when a customer says their laptop was
 * stolen, and the order of the cards is that conversation: the account first,
 * then the devices, then the bindings and sessions those devices hold.
 *
 * Subscriptions sit second, directly under the account, because the OTHER
 * half of the support queue is a billing question — "what am I paying for and
 * why did it not renew" — and the answer to that has nothing to do with what
 * is installed. It is its own query behind its own permission; see
 * SubscriptionsCard.
 *
 * The event trail sits directly BELOW the devices, and that order is the
 * argument: the installation is the thing an operator acts on, the trail is
 * evidence about it. Above the table it would put a hundred lines of history
 * between the operator and the two buttons they came here to press; below it,
 * an event that names a device links back UP to the row it is evidence for.
 */
export function UserDetail({ userId, search }: { userId: string; search: AnalyticsSearch }) {
  return (
    <RequireOperator>
      {(session) => <UserPanels userId={userId} search={search} operator={session.operator} />}
    </RequireOperator>
  );
}

function UserPanels({
  userId,
  search,
  operator,
}: {
  userId: string;
  search: AnalyticsSearch;
  operator: Operator;
}) {
  const user = useUser(userId);
  // One banner for the page. Revocations happen in four cards and land here,
  // so the counts are read in the same place whichever button produced them.
  const [notice, setNotice] = useState<string | null>(null);

  if (user.isPending) {
    return <Spinner label="Loading the account" />;
  }
  if (user.error || !user.data) {
    return <ErrorAlert error={user.error} title="Could not load the account" />;
  }

  const data: UserDetailData = user.data;

  return (
    <>
      <PageHeader
        title={data.user.display_name || data.user.primary_email || data.user.id}
        description={
          <>
            <code>{data.user.id}</code> · <Link to="/users">back to search</Link>
          </>
        }
        actions={<StatusBadge status={data.user.status} />}
      />
      {notice ? <Alert tone="success" title={notice} /> : null}
      <ProfileCard data={data} operator={operator} onDone={setNotice} />
      <SubscriptionsCard userId={userId} operator={operator} />
      {/* Absent, not empty, without the grant — see UsageCard. The check is
          here rather than inside it so the query is never issued either: the
          Admin API's Guard writes every refusal to the audit trail, and a
          console that fires a read it knows will be refused fills that trail
          with denials nobody chose to attempt. */}
      {can(operator, READ) ? <UsageCard userId={userId} search={search} /> : null}
      <DevicesCard
        userId={userId}
        devices={data.devices}
        bindings={data.bindings}
        userStatus={data.user.status}
        operator={operator}
        onDone={setNotice}
      />
      <SecurityEventsCard userId={userId} devices={data.devices} operator={operator} />
      <BindingsCard
        userId={userId}
        bindings={data.bindings}
        devices={data.devices}
        operator={operator}
        onDone={setNotice}
      />
      <SessionsCard userId={userId} sessions={data.sessions} operator={operator} onDone={setNotice} />
      <EntitlementsCard entitlements={data.entitlements} />
    </>
  );
}

function ProfileCard({
  data,
  operator,
  onDone,
}: {
  data: UserDetailData;
  operator: Operator;
  onDone: (message: string) => void;
}) {
  const suspend = useSuspendUser();
  const reactivate = useReactivateUser();
  const developerFlag = useSetUserDeveloperFlag();
  const suspended = data.user.status === "suspended";
  const developer = data.user.is_empite_developer;
  const name = data.user.display_name || data.user.primary_email || data.user.id;

  return (
    <Card title="Account">
      <ErrorAlert
        error={suspend.error ?? reactivate.error ?? developerFlag.error}
        title="That change failed"
      />
      <dl className="esm-detail-list">
        <dt>Status</dt>
        <dd>
          <StatusBadge status={data.user.status} />
        </dd>
        <dt>Registration state</dt>
        <dd>
          <code>{data.user.registration_state}</code>
        </dd>
        {/* Stated in both directions rather than shown only when true. "No
            badge" and "this console did not ask" look the same, and an
            operator about to grant testing features to a stranger's account
            needs to see the answer, not the absence of one. */}
        <dt>Empite developer</dt>
        <dd>{developer ? <StatusBadge status="developer" /> : "No"}</dd>
        <dt>Joined</dt>
        <dd>{when(data.user.created_at)}</dd>
        <dt>Last sign-in</dt>
        <dd>{when(data.user.last_login_at)}</dd>
        <dt>Email</dt>
        <dd>
          {data.emails.length === 0
            ? "No address on file"
            : data.emails.map((email) => (
                <div key={email.id}>
                  {email.address}{" "}
                  <Badge tone={email.verified ? "success" : "warning"}>
                    {email.verified ? "verified" : "unverified"}
                  </Badge>
                  {email.is_primary ? <Badge>primary</Badge> : null}
                </div>
              ))}
        </dd>
        <dt>Phone</dt>
        <dd>
          {/* The last digits are all there is to show. The number itself is
              stored encrypted and no admin route returns a readable one — so
              an operator cannot read a customer's phone number out of this
              console, and a compromised operator account cannot either. */}
          {data.phone ? (
            <>
              +{data.phone.country_code} ••• {data.phone.last_digits}{" "}
              <Badge tone={data.phone.verified ? "success" : "warning"}>
                {data.phone.verified ? "verified" : "unverified"}
              </Badge>
            </>
          ) : (
            "No number on file"
          )}
        </dd>
      </dl>
      <div className="esm-actions">
        {can(operator, SUSPEND) ? (
          suspended ? (
            <ReasonButton
              variant="primary"
              busy={reactivate.isPending}
              mutation={{
                title: `Reactivate ${name}`,
                description: (
                  <p>
                    The account can sign in again. Devices, bindings and sessions that were revoked
                    by the suspension stay revoked — reactivating does not undo them.
                  </p>
                ),
                confirmLabel: "Reactivate account",
                run: async (reason) => {
                  const result = await reactivate.mutateAsync({ userID: data.user.id, reason });
                  onDone(`${name} is ${result.status} again.`);
                },
              }}
            >
              Reactivate account
            </ReasonButton>
          ) : (
            <ReasonButton
              variant="danger"
              busy={suspend.isPending}
              mutation={{
                title: `Suspend ${name}`,
                description: (
                  <p>
                    This signs the account out everywhere at once: every application session, every
                    browser sign-in and every refresh family. They cannot sign in again until an
                    operator reactivates them.
                  </p>
                ),
                confirmLabel: "Suspend account",
                run: async (reason) => {
                  const result = await suspend.mutateAsync({ userID: data.user.id, reason });
                  onDone(revocationSummary(`${name} suspended`, result));
                },
              }}
            >
              Suspend account
            </ReasonButton>
          )
        ) : (
          <span className="esm-muted-text">
            Your roles do not grant <code>{SUSPEND}</code>.
          </span>
        )}
        {/* Hidden, not disabled, for an operator without the permission — a
            greyed-out button advertises a lever they cannot pull and invites
            the "who do I ask" conversation the roles already answer. The
            server re-checks either way and records the refusal. */}
        {can(operator, DEVELOPER_FLAG) ? (
          <ReasonButton
            busy={developerFlag.isPending}
            mutation={{
              title: developer
                ? `Remove the developer marker from ${name}`
                : `Mark ${name} as an Empite developer`,
              description: developer ? (
                <p>
                  Their applications stop reporting them as an Empite developer at the next
                  sign-in, and the testing features that marker unlocks go away with it.
                </p>
              ) : (
                <p>
                  Every application this account signs in to will report them as an Empite
                  developer and unlock its testing features. This is global — it is not granted
                  per application.
                </p>
              ),
              confirmLabel: developer ? "Remove the marker" : "Mark as a developer",
              // The state we want, sent outright. The route is a SET rather
              // than a flip so two tabs open on this page cannot resolve to
              // whichever request lost the race.
              run: async (reason) => {
                const result = await developerFlag.mutateAsync({
                  userID: data.user.id,
                  isEmpiteDeveloper: !developer,
                  reason,
                });
                onDone(developerNotice(name, result));
              },
            }}
          >
            {developer ? "Remove developer marker" : "Mark as an Empite developer"}
          </ReasonButton>
        ) : null}
      </div>
    </Card>
  );
}

/**
 * What the banner says after the flag is written.
 *
 * `changed: false` gets its own sentence because the server writes no audit
 * row for a replay — reporting "marked as an Empite developer" there would
 * promise a trail entry that does not exist, and an operator who clicked
 * twice deserves to know the second click did nothing.
 */
function developerNotice(name: string, result: UserDeveloperFlagResult): string {
  if (!result.changed) {
    return result.is_empite_developer
      ? `${name} was already marked as an Empite developer — nothing changed.`
      : `${name} was already not an Empite developer — nothing changed.`;
  }
  return result.is_empite_developer
    ? `${name} is now marked as an Empite developer.`
    : `${name} is no longer marked as an Empite developer.`;
}

/** The permission this card's data sits behind. `packages:read`, not
 *  `users:read`: what an account PAYS FOR is a different thing to be trusted
 *  with than who they are — see the route in internal/admin/http.go. */
const PACKAGES = "packages:read";

/**
 * What this account is subscribed to, per application.
 *
 * ITS OWN QUERY, for the reason the wallet page's ledger is its own query: it
 * is behind a permission this page does not otherwise need, so a role that may
 * end a stolen laptop's session but not read the retail catalogue gets a
 * notice in this one card and a working page everywhere else. Folded into the
 * account read it would have failed the whole screen for them.
 *
 * TERMINAL SUBSCRIPTIONS ARE SHOWN, below the live ones the server sorts to
 * the top. "They cancelled last month" is half the answers this card exists to
 * give, and an account that cancelled yesterday must not render identically to
 * one that never subscribed.
 *
 * The provider ids are rendered in full and copyable, because the reason an
 * operator is on this card at all is usually to open the same record in the
 * Stripe dashboard, and a truncated `cus_` pasted there finds nothing and does
 * not say why. Nothing else of the provider's is here: no secret, no webhook
 * payload, no checkout URL, and a card that is a brand and four digits because
 * four digits is the whole width this platform is able to store.
 */
function SubscriptionsCard({ userId, operator }: { userId: string; operator: Operator }) {
  const mayRead = can(operator, PACKAGES);
  // The query is not even issued for an operator who cannot read it: the
  // server would refuse, and spending a round trip to be told so puts a 403
  // in the network log of a console that is behaving correctly.
  const subscriptions = useUserSubscriptions(userId, mayRead);

  if (!mayRead) {
    return (
      <Card title="Subscriptions">
        <Alert tone="info" title="Not your permission">
          Reading what an account subscribes to needs packages:read, which your roles do not
          grant. The rest of this page needs only users:read.
        </Alert>
      </Card>
    );
  }
  if (subscriptions.error) {
    // A 403 can still arrive: the permission list this console holds is the
    // one from sign-in, and a role changed underneath an open page is exactly
    // the case where the server's answer is the true one.
    const forbidden = isApiError(subscriptions.error) && subscriptions.error.status === 403;
    return (
      <Card title="Subscriptions">
        <Alert
          tone={forbidden ? "info" : "danger"}
          title={forbidden ? "Not your permission" : "Could not load the subscriptions"}
        >
          {forbidden
            ? "Reading what an account subscribes to needs packages:read, which your roles do not grant."
            : errorMessage(subscriptions.error)}
        </Alert>
      </Card>
    );
  }
  if (subscriptions.isPending) {
    return (
      <Card title="Subscriptions">
        <Spinner label="Loading the subscriptions" />
      </Card>
    );
  }

  const rows = subscriptions.data?.subscriptions ?? [];
  if (rows.length === 0) {
    // A sentence, not an empty table. "No subscription" is an answer; an empty
    // table with six headers reads as a page that failed to load.
    return (
      <Card title="Subscriptions">
        <p className="esm-muted-text">
          No subscription. This account has never subscribed to a package in any application —
          any credit it holds was bought at the top-up counter.
        </p>
      </Card>
    );
  }

  return (
    <Card title="Subscriptions">
      {rows.map((row) => (
        <SubscriptionFacts key={row.id} subscription={row} />
      ))}
    </Card>
  );
}

/**
 * One subscription, as a fact list rather than a table row.
 *
 * A table would have to put a `cus_`, a `sub_`, a period and a card in six
 * columns and truncate all of them. These are read one at a time — an operator
 * is looking at ONE customer's ONE agreement — so the shape that fits is the
 * one the account card above already uses.
 */
function SubscriptionFacts({ subscription }: { subscription: CustomerSubscription }) {
  const period =
    subscription.current_period_start && subscription.current_period_end
      ? `${when(subscription.current_period_start)} → ${when(subscription.current_period_end)}`
      : // Both ends are null together — 0019 refuses a row that knows one and
        // not the other — and a subscription in that state has not had its
        // first invoice paid yet.
        "No period yet — the first invoice has not been paid";
  const card =
    subscription.payment_method_brand || subscription.payment_method_last4
      ? `${subscription.payment_method_brand || "card"} ···· ${subscription.payment_method_last4 || "????"}`
      : "The provider has not told us how this is paid for yet";

  return (
    <section className="esm-subscription">
      <h3 className="esm-subscription__title">
        {subscription.application_name}{" "}
        <span className="esm-muted-text">({subscription.application_slug})</span>{" "}
        <StatusBadge status={subscription.status} />
        {/* Two different facts and two different badges. `cancel_at_period_end`
            is a LIVE subscription that will not renew — the customer still has
            what they paid for — and it must not look like one that has already
            ended. */}
        {subscription.cancel_at_period_end && subscription.live ? (
          <Badge tone="warning">cancelling at period end</Badge>
        ) : null}
      </h3>
      <dl className="esm-detail-list">
        <dt>Package</dt>
        <dd>
          <Link to="/packages/$packageId" params={{ packageId: subscription.package_id }}>
            {subscription.package_name}
          </Link>{" "}
          <span className="esm-muted-text">({subscription.package_slug})</span>
        </dd>
        {/* The PINNED terms — what this subscriber was sold, not what the
            package charges today. A re-price appends a version and leaves
            existing subscribers on the one they signed up at, so quoting the
            catalogue's current price here would be quoting a number the
            customer's card statement disagrees with. */}
        <dt>Terms</dt>
        <dd>
          <span className="esm-amount">
            {subscription.price} <span className="esm-muted-text">{subscription.currency}</span>
          </span>{" "}
          per {subscription.interval_count === 1 ? "" : `${subscription.interval_count} `}
          {subscription.billing_interval}
          {subscription.interval_count === 1 ? "" : "s"}, granting{" "}
          <span className="esm-amount">
            {subscription.granted_credit}{" "}
            <span className="esm-muted-text">{subscription.currency}</span>
          </span>{" "}
          of subscription credit each period.
        </dd>
        <dt>Current period</dt>
        <dd>{period}</dd>
        <dt>Renewal</dt>
        <dd>
          {subscription.cancelled_at
            ? `Cancelled ${when(subscription.cancelled_at)}`
            : subscription.cancel_at_period_end
              ? `Will NOT renew — ends ${when(subscription.current_period_end)}`
              : subscription.live
                ? `Renews ${when(subscription.current_period_end)}`
                : "Not renewing"}
        </dd>
        <dt>Paid by</dt>
        <dd>{card}</dd>
        <dt>Wallet</dt>
        <dd>
          {/* Where the allowance lands. The ledger behind this link is the
              proof that the grant actually happened, which is the next thing
              asked after "was I charged". */}
          <Link to="/wallets/$walletId" params={{ walletId: subscription.wallet_id }}>
            View the wallet and its ledger
          </Link>
        </dd>
        <dt>Subscription id</dt>
        <dd>
          <CopyText value={subscription.id} label="the subscription id" />
        </dd>
        <dt>{subscription.provider} customer</dt>
        <dd>
          {subscription.provider_customer_id ? (
            <CopyText value={subscription.provider_customer_id} label="the provider customer id" />
          ) : (
            <span className="esm-muted-text">
              Not created yet — the provider has not been reached for this subscription.
            </span>
          )}
        </dd>
        <dt>{subscription.provider} subscription</dt>
        <dd>
          {subscription.provider_subscription_id ? (
            <CopyText
              value={subscription.provider_subscription_id}
              label="the provider subscription id"
            />
          ) : (
            <span className="esm-muted-text">
              Not bound yet — no invoice has been paid against this subscription.
            </span>
          )}
        </dd>
      </dl>
    </section>
  );
}

/* -------------------------------------------------------------------------- *
 * Usage.
 *
 * The account's own slice of the analytics surface, on the page an operator is
 * already on when somebody asks why their bill is what it is. It sits directly
 * under the subscriptions for that reason: those two cards answer the same
 * half of the support queue — "what am I paying for" and "what did I use" —
 * and the devices below them answer the other half.
 *
 * AGGREGATES ONLY, and structurally so. The route behind this card returns no
 * request list, no message count, no prompt and no completion, so there is
 * nothing here for a control to drill into: the deepest thing on the card is a
 * per-alias token count. That is not an omission from this screen, it is the
 * shape of the document, and there is no route that would answer otherwise.
 * -------------------------------------------------------------------------- */

/** How many aliases the model split draws. Eight bars is a chart; every alias
 *  the account has ever asked for is a list, and this card is not one. */
const TOP_ALIASES = 8;

/** Counts, with separators. One formatter for the sparkline and both splits,
 *  so an axis figure and a hidden-table cell cannot disagree about 12000. */
function count(value: number): string {
  return value.toLocaleString();
}

/**
 * One account's usage over a window the operator chooses.
 *
 * ITS OWN QUERY, on the rule SubscriptionsCard above sets and for a sharper
 * reason: this read is behind a grant the rest of the page does not need, it
 * is the only read on the page that takes a window, and it is the only one an
 * operator can make fail by hand-editing a URL. Folded into the account read
 * it would have taken the devices, the bindings and the sessions down with it
 * — the three things somebody whose laptop was stolen is on this page for.
 *
 * NOT RENDERED AT ALL without `analytics:read`; see UserPanels. An empty card
 * headed "Usage" would say this account used nothing, which is a claim about
 * the customer rather than about the reader's permissions, and the placeholder
 * that would fix that is a second card telling an operator about a grant they
 * did not come here to think about. The devices card below is the one they
 * came for.
 */
function UsageCard({ userId, search }: { userId: string; search: AnalyticsSearch }) {
  const navigate = useNavigate();
  const usage = useAnalyticsUserDetail(userId, {
    since: search.since,
    until: search.until,
    days: search.days,
  });
  const data = usage.data;

  // Five states and not two, on the dashboard's rule. A failed read is not an
  // unmeasured window; an unmeasured window is not a quiet account; and a
  // quiet account is not a zeroed one. `available_from` is the platform's
  // earliest rollup day, so it separates "nobody had measured yet" from "this
  // person did nothing" — and it is asked BEFORE hasUsage, because a document
  // full of zeros satisfies both.
  const measured = data !== undefined && windowMeasured(data.available_from, data.window.until);
  const used = data !== undefined && hasUsage(data);

  // Keyed on the document itself, so a keystroke in the window picker beside
  // this — which is another component's state — cannot rebuild a series.
  const requests = useMemo(() => (data ? userRequestSeries(data.series) : []), [data]);
  const applications = useMemo(() => (data ? applicationParts(data.applications) : []), [data]);
  const models = useMemo(
    () => (data ? modelParts(data.models, TOP_ALIASES) : { points: [], aliases: 0 }),
    [data],
  );

  // The response ALONE, with no prediction from the operator's own permission
  // list. That two-authority dance is for a whole CARD, which appears or does
  // not and shoves the page around when it changes its mind; a withheld FIELD
  // has a slot to sit in either way, and the slot says why it is empty. The
  // `false` is not a default anybody reads: no tile is drawn before `data`
  // arrives, so there is no figure for it to be wrong about.
  const moneyVisible = data?.money_visible ?? false;
  const currency = data?.currency;
  // A 403 can still arrive: the permission list this console holds is the one
  // from sign-in, and a role changed underneath an open page is exactly the
  // case where the server's answer is the true one.
  const forbidden = isApiError(usage.error) && usage.error.status === 403;

  return (
    <Card title="Usage">
      <p className="esm-muted-text">
        What this account has actually used, from the worker's nightly rollups. Every figure is an
        aggregate: there is no request, no prompt and no completion in this document, and nothing
        on this card opens one.
      </p>

      {/* The window is in the URL rather than in this card's state, so a
          narrowed span can be pasted into a ticket and opened by the next
          person on the same days. The rest of the page ignores it. */}
      <WindowPicker
        search={search}
        busy={usage.isFetching}
        idPrefix="usage"
        apply={(next) => void navigate({ to: "/users/$userId", params: { userId }, search: next })}
      />

      {usage.error ? (
        <Alert
          tone={forbidden ? "info" : "danger"}
          title={forbidden ? "Not your permission" : "The usage figures could not be loaded"}
        >
          {forbidden
            ? `Reading an account's usage needs ${READ} and ${IDENTITY}, and your roles no longer grant both. Everything else on this page is unaffected.`
            : errorMessage(usage.error)}
        </Alert>
      ) : null}

      {/* Drawn from the answer's own window, never from the ask: a card that
          was clamped says so itself rather than borrowing a claim from the
          URL that asked. */}
      {data ? (
        <WindowNotices
          window={data.window}
          currency={data.currency}
          currencies={data.currencies}
          search={search}
        />
      ) : null}

      {usage.isPending && !usage.error ? <Spinner label="Loading the usage figures" /> : null}

      {data && !measured ? (
        <EmptyState title="Nothing has been measured over these days">
          <p>
            {data.available_from === null
              ? "No daily rollup has been written yet, so there is nothing to report about anybody — not even a zero. The worker writes them nightly."
              : `The rollups start on ${data.available_from}, which is after this window ends. Ask about a later window to see figures.`}
          </p>
          <p>
            This is not the same as this account having been idle. Zeros are not shown here on
            purpose: they would say the customer used nothing on days nobody was measuring.
          </p>
        </EmptyState>
      ) : null}

      {data && measured && !used ? (
        <EmptyState title="No usage in this window">
          <p>
            The rollups cover {data.window.since} to {data.window.until} and this account made no
            request in them. That is a measurement, not a missing answer — the read succeeded.
          </p>
          <p>Ask about a longer window, or an earlier one, if they used to be active.</p>
        </EmptyState>
      ) : null}

      {data && measured && used ? (
        <>
          <div className="esm-stat-grid">
            <StatTile
              label="Requests"
              value={count(data.totals.requests)}
              hint={`${count(data.totals.failed_requests)} of them failed.`}
            />
            <StatTile
              label="Failure rate"
              value={formatRate(data.totals.failure_rate)}
              unavailable="The rate could not be computed from this answer."
              hint="Failed requests over requests, inside this window."
            />
            <StatTile
              label="Total tokens"
              value={count(data.totals.total_tokens)}
              hint={`${count(data.totals.input_tokens)} in, ${count(data.totals.output_tokens)} out, ${count(data.totals.cached_input_tokens)} cached, ${count(data.totals.cache_write_tokens)} cache writes, ${count(data.totals.reasoning_tokens)} reasoning.`}
            />
            <StatTile
              // "up to", because the figure is an upper bound and the wire
              // says so in its name: the rollup stores a per-day distinct
              // count and never the ids, so an alias used on ten days
              // contributes ten. The exact list is the split below.
              label="Models"
              value={`up to ${count(data.totals.distinct_models_upper_bound)}`}
              hint="An upper bound. The aliases themselves are in the split below, which is counted exactly."
            />
            <StatTile
              label="Devices"
              value={`up to ${count(data.totals.distinct_devices_upper_bound)}`}
              hint="An upper bound over the window. Which machines they are on now is the devices card below, which is a live snapshot rather than a window figure."
            />
            <StatTile
              label="Provider cost"
              value={money(data.totals.cost_micros, moneyVisible, currency)}
              unavailable={WITHHELD}
              hint="What this account's requests cost upstream."
            />
            <StatTile
              label="Revenue"
              value={money(data.totals.sell_micros, moneyVisible, currency)}
              unavailable={WITHHELD}
              hint="What they were charged for them. Top-ups and subscription charges are money moving into a wallet rather than revenue, and are not in this figure."
            />
            <StatTile
              label="Margin"
              value={money(data.totals.margin_micros, moneyVisible, currency)}
              unavailable={WITHHELD}
              hint="Revenue minus cost. Negative is a real reading — a written-off request costs money that was never charged for."
            />
          </div>

          <p className="esm-muted-text">
            {data.first_request_at && data.last_request_at ? (
              <>
                Active between {when(data.first_request_at)} and {when(data.last_request_at)},
                inside this window.
              </>
            ) : (
              // Not "never": the rollup rows for this account carry no
              // timestamps, which is a gap in the measurement rather than a
              // fact about when they last used the platform.
              <>The rollups for these days recorded no request times for this account.</>
            )}
          </p>

          <h3>Requests per day</h3>
          <p className="esm-muted-text">
            Every day of the window, including the quiet ones — a day with no traffic is drawn as
            the zero it was measured to be, never as a break in the line. Shape only; the totals
            are in the tiles above.
          </p>
          <Sparkline
            name="Requests per day"
            points={requests}
            format={count}
            width={720}
            height={120}
            pointHeader="Day"
            valueHeader="Requests"
          />

          <h3>Where the tokens went</h3>
          <p className="esm-muted-text">
            One bar per application this account used, measured in tokens rather than requests
            because tokens are what the platform bills for. Every application they touched is
            drawn — this list is not cut.
          </p>
          {applications.length === 0 ? (
            <p className="esm-muted-text">
              No application row was recorded for these days, which is odd beside the traffic
              above rather than an answer — the two come from the same rollup.
            </p>
          ) : allZero(applications) ? (
            <p className="esm-muted-text">
              Every application reads zero tokens: this account&rsquo;s requests in this window all
              failed before a token was counted. The request counts are in the tiles above.
            </p>
          ) : (
            <BarChart
              name="Tokens by application"
              points={applications}
              format={count}
              pointHeader="Application"
              valueHeader="Tokens"
              // Wide enough for a slug and for a seven-digit token count.
              labelColumn={200}
              valueGutter={110}
            />
          )}

          <h3>What they asked for</h3>
          <p className="esm-muted-text">
            The model ALIAS the client asked for, which is the product — one alias can be answered
            by several provider models through fallback, and this counts the ask. Ranked by tokens,
            which is the order the server ranked and cut them in.
          </p>
          {data.models_truncated ? (
            <Alert tone="info" title="The model list was cut short">
              This account asked for more aliases than one answer returns, and the tail it dropped
              is the lightest end of it by tokens. An alias missing below is one this card does not
              cover — not one at zero.
            </Alert>
          ) : null}
          {models.points.length === 0 ? (
            <p className="esm-muted-text">
              No request in this window named an alias the request log still holds. This breakdown
              is computed from that log rather than from the rollups, and it is trimmed on its own
              schedule — so an empty split under a non-zero request count is a retention boundary
              rather than a contradiction.
            </p>
          ) : allZero(models.points) ? (
            <p className="esm-muted-text">
              Every alias reads zero tokens: nothing this account asked for in this window returned
              one. The request counts are in the tiles above.
            </p>
          ) : (
            <>
              <BarChart
                name="Tokens by model alias"
                points={models.points}
                format={count}
                pointHeader="Alias"
                valueHeader="Tokens"
                // Long enough for a provider-qualified alias.
                labelColumn={220}
                valueGutter={110}
              />
              {models.aliases > models.points.length ? (
                <p className="esm-chart__note">
                  {models.aliases - models.points.length} lighter{" "}
                  {models.aliases - models.points.length === 1 ? "alias is" : "aliases are"} not
                  drawn. The total in the summary is the total of the bars above, not of the
                  account.
                </p>
              ) : null}
            </>
          )}
        </>
      ) : null}
    </Card>
  );
}

/**
 * The installations this account has ever been bound to, everything the
 * platform holds about each one, and the three things an operator can do to it.
 *
 * The card has to keep three facts apart, because conflating any two of them is
 * how somebody bricks a customer's laptop:
 *
 * 1. The INSTALLATION's status. Active means it may be bound; revoked means it
 *    was BLOCKED, permanently, for everybody.
 * 2. Who is SIGNED IN on it. A device belongs to nobody — only a live binding
 *    makes it somebody's — and this list includes installations this user has
 *    merely used, so the occupant may well be a different account.
 * 3. Whether the ACCOUNT is active. Suspending a user does not touch their
 *    bindings by design (SuspendUser leaves them alone and relies on
 *    SessionIsLive refusing on the user's status), so a suspended account's
 *    devices stay "signed in" in the database while none of them can actually
 *    do anything. The card says so rather than letting the two controls tell
 *    an operator different stories.
 *
 * A SECTION PER DEVICE, not a table row, and that is forced by what the
 * Admin API now sends: seventeen scalar fields, a signing-key history and an
 * approval history per installation. Seventeen columns is seventeen truncated
 * cells, and the two histories have no column shape at all. This is the trade
 * SubscriptionFacts above already makes for the same reason — these are read
 * one at a time, because an operator is looking at ONE machine.
 *
 * The three histories sit behind a <details> each. Two of them are bounded per
 * device server-side (20 keys, 50 approvals) and a user may have up to 200
 * devices, so rendering every history open would put ten thousand rows between
 * the operator and the Block button. The disclosure summary carries the COUNT,
 * so "this device has no key" is readable without opening anything.
 *
 * The third — app metadata — is a separate read behind a separate permission,
 * and its disclosure does more than hide rows: the query is not issued until
 * it is opened, because each one writes an audit row. See DeviceAppMetadata.
 */
function DevicesCard({
  userId,
  devices,
  bindings,
  userStatus,
  operator,
  onDone,
}: {
  userId: string;
  devices: UserDevice[];
  bindings: UserBinding[];
  userStatus: string;
  operator: Operator;
  onDone: (message: string) => void;
}) {
  const signOut = useSignOutDevice();
  const restore = useRestoreDevice();
  const revoke = useRevokeDevice();
  const mayRevoke = can(operator, REVOKE);
  const mayReadMetadata = can(operator, DEVICES_READ);
  const accountBlocked = userStatus !== "active";

  // The account's bindings, grouped by the installation they sit on. The
  // BINDINGS CARD below still owns them — this is a cross-link, not a second
  // copy of the table: each device names the applications bound to it and
  // links down to the row that can revoke one.
  const boundHere = new Map<string, UserBinding[]>();
  for (const binding of bindings) {
    const held = boundHere.get(binding.device_id);
    if (held) {
      held.push(binding);
    } else {
      boundHere.set(binding.device_id, [binding]);
    }
  }

  return (
    <Card title="Devices">
      <ErrorAlert error={signOut.error} title="That device could not be signed out" />
      <ErrorAlert error={revoke.error} title="That device could not be blocked" />
      {/* The restore route can refuse too — it is the one an operator reaches
          for a device somebody else blocked, and the server says no. Without
          this the dialog just closed and nothing happened. */}
      <ErrorAlert error={restore.error} title="That device could not be restored" />
      {accountBlocked ? (
        <Alert tone="warning" title={`This account is ${userStatus}`}>
          Every session below is already refused — the account's status is checked on every
          request, so nothing here can be used until it is reactivated. Signing a device out or
          blocking it is still a separate decision about the machine, which outlives the
          suspension.
        </Alert>
      ) : null}
      {/* Signed-out and blocked devices stay in the list. An operator has to
          be able to see that the laptop the customer called about is already
          dealt with — a row that disappears on success is indistinguishable
          from a row that was never there. */}
      {devices.length === 0 ? (
        <p className="esm-muted-text">
          No application has been installed on a device for this account.
        </p>
      ) : (
        devices.map((device) => (
          <DeviceFacts
            key={device.id}
            userId={userId}
            device={device}
            bindings={boundHere.get(device.id) ?? []}
            accountBlocked={accountBlocked}
            mayRevoke={mayRevoke}
            mayReadMetadata={mayReadMetadata}
            signOut={signOut}
            restore={restore}
            revoke={revoke}
            onDone={onDone}
          />
        ))
      )}
    </Card>
  );
}

/**
 * One installation, whole.
 *
 * The header carries the two things an operator scans for — the machine's name
 * and whether it is still allowed to bind — and the actions sit directly under
 * it, so the Block button is reachable without reading anything. Everything
 * else is a fact list underneath, in the order the questions get asked: who is
 * on it, what is this machine, what is it running, where has it been seen, and
 * what is bound to it.
 */
function DeviceFacts({
  userId,
  device,
  bindings,
  accountBlocked,
  mayRevoke,
  mayReadMetadata,
  signOut,
  restore,
  revoke,
  onDone,
}: {
  userId: string;
  device: UserDevice;
  bindings: UserBinding[];
  accountBlocked: boolean;
  mayRevoke: boolean;
  mayReadMetadata: boolean;
  signOut: ReturnType<typeof useSignOutDevice>;
  restore: ReturnType<typeof useRestoreDevice>;
  revoke: ReturnType<typeof useRevokeDevice>;
  onDone: (message: string) => void;
}) {
  const name = device.device_name || device.installation_id;

  return (
    // The anchor the event trail below links to. It is the whole section now
    // rather than a table cell, so an event that names a device lands on
    // everything known about it instead of on six columns.
    <section id={deviceAnchorID(device.id)} className="esm-device esm-anchor">
      <h3 className="esm-device__title">
        <span>{name}</span>
        {device.status === "revoked" ? (
          <Badge tone="danger">blocked — cannot bind again</Badge>
        ) : (
          <StatusBadge status={device.status} />
        )}
      </h3>
      <DeviceActions
        userId={userId}
        device={device}
        mayRevoke={mayRevoke}
        signOut={signOut}
        restore={restore}
        revoke={revoke}
        onDone={onDone}
      />
      <dl className="esm-detail-list">
        <dt>Installation id</dt>
        <dd>
          {/* Copyable and whole: this is the id an operator carries out of the
              console into the gateway logs, and a truncated one finds nothing
              there and does not say why. */}
          <CopyText value={device.installation_id} label="the installation id" />
        </dd>
        <dt>Device id</dt>
        <dd>
          {/* What the bindings table below and the security trail both key
              on — the value to match a row against by eye. */}
          <CopyText value={device.id} label="the device id" />
        </dd>
        {/* Occupancy is a fact row and not a header badge because Occupancy
            renders a sentence under its badge — who the other account is, and
            since when — and a <div> is not allowed inside an <h3>. It sits
            first because "is anybody on this machine right now" is the
            question the two buttons above are pressed to answer. */}
        <dt>Signed in</dt>
        <dd>
          <Occupancy device={device} accountBlocked={accountBlocked} />
        </dd>
        <dt>Platform</dt>
        <dd>
          {/* platform is NOT NULL in auth.devices; os_version and app_version
              are COALESCEd to "" by the Admin API, so a blank one of those
              cannot be told from an unreported one on the wire — hence the
              plain "not recorded" rather than the two-valued Reported below. */}
          {device.platform} {device.os_version || <NotRecorded />}
        </dd>
        <dt>App version</dt>
        <dd>{device.app_version || <NotRecorded />}</dd>
        <dt>Hardware model</dt>
        <dd>
          <Reported value={device.hardware_model} />
        </dd>
        <dt>CPU architecture</dt>
        <dd>
          <Reported value={device.cpu_architecture} mono />
        </dd>
        <dt>Hostname</dt>
        <dd>
          <Reported value={device.hostname} mono />
        </dd>
        <dt>Locale</dt>
        <dd>
          <Reported value={device.locale} mono />
        </dd>
        <dt>Timezone</dt>
        <dd>
          <Reported value={device.timezone} mono />
        </dd>
        <dt>First seen</dt>
        <dd>{when(device.first_seen_at)}</dd>
        <dt>Last seen</dt>
        <dd>{when(device.last_seen_at)}</dd>
        <dt>Last-seen IP</dt>
        <dd>
          {/* "Last-seen" and not "registered from": the column is re-read on
              every proof use, so what it holds is where the machine was the
              last time it spoke — which is the fact an incident turns on. */}
          <Reported value={device.last_seen_ip} mono />
        </dd>
        <dt>Last-seen user agent</dt>
        <dd>
          {/* One line with the whole value on hover, like ShortID: a user
              agent is 150 characters of no interest until it is the fact
              being checked, and wrapped in full it pushes the histories off
              the screen. */}
          <Reported value={device.last_seen_user_agent} mono truncate />
        </dd>
        {device.status !== "active" ? (
          <>
            <dt>Blocked because</dt>
            {/* Whose decision it was. It is what the Restore button is offered
                from: the customer's own retire is undoable, an operator's block
                is not. */}
            <dd>{device.revoked_reason || <NotRecorded />}</dd>
          </>
        ) : null}
        <dt>Bindings</dt>
        <dd>
          <DeviceBindings bindings={bindings} />
        </dd>
      </dl>
      <DeviceKeys keys={device.keys} />
      <DeviceApprovals approvals={device.approvals} />
      {/* Absent, not empty, without devices:read — see DEVICES_READ. It is
          also the only thing on this card that is not already in hand: it has
          its own query, its own permission and its own failure, and it fails
          without taking the two histories above it or the Block button with
          it. */}
      {mayReadMetadata ? <DeviceAppMetadata deviceID={device.id} /> : null}
    </section>
  );
}

/** The sign-out, block and restore controls, unchanged — lifted out of the
 *  table cell they used to be rendered in so the fact list above stays
 *  readable. Every guard here decides what is DRAWN; the Admin API decides
 *  what is permitted, and refuses the same three cases regardless. */
function DeviceActions({
  userId,
  device,
  mayRevoke,
  signOut,
  restore,
  revoke,
  onDone,
}: {
  userId: string;
  device: UserDevice;
  mayRevoke: boolean;
  signOut: ReturnType<typeof useSignOutDevice>;
  restore: ReturnType<typeof useRestoreDevice>;
  revoke: ReturnType<typeof useRevokeDevice>;
  onDone: (message: string) => void;
}) {
  const active = device.status === "active";
  // A blocked installation an OPERATOR blocked offers nothing at all — sign
  // out has nobody to sign out, block has already happened and restore is
  // refused by the server. Rendering the empty row anyway would leave a gap
  // under the heading that reads as a control that failed to draw.
  const mayRestore = !active && device.revoked_reason === "user_retired_device";
  if (!mayRevoke || (!device.signed_in && !active && !mayRestore)) {
    return null;
  }
  const name = device.device_name || device.installation_id;
  return (
    <div className="esm-actions">
      {/* Sign out is offered only when somebody is actually on the machine: on
          an empty installation it would revoke nothing and report "0 sessions
          ended", which reads as a failure. */}
      {device.signed_in ? (
        <ReasonButton
          variant="secondary"
          busy={signOut.isPending && signOut.variables?.deviceID === device.id}
          mutation={{
            title: `Sign out ${name}`,
            tone: "primary",
            description: (
              <p>
                Ends {device.occupant_is_user ? "this account's" : "the current user's"} session on
                this installation and revokes the refresh tokens behind it. The installation stays
                registered: <strong>they can sign in again on the same machine</strong>, and so can
                anybody else. Use this for "someone else needs this laptop" and for clearing a
                stuck session.
              </p>
            ),
            confirmLabel: "Sign out",
            run: async (reason) => {
              const result = await signOut.mutateAsync({
                deviceID: device.id,
                reason,
                userID: userId,
              });
              onDone(revocationSummary(`${name} signed out`, result));
            },
          }}
        >
          Sign out
        </ReasonButton>
      ) : null}
      {active ? (
        <ReasonButton
          variant="danger"
          busy={revoke.isPending && revoke.variables?.deviceID === device.id}
          mutation={{
            title: `Block ${name}`,
            description: (
              <>
                <p>
                  This is the stolen-laptop lever, and <strong>there is no undo</strong>. It ends
                  every session and binding on the installation, for every account that has ever
                  used it, and retires the installation's key.
                </p>
                <p>
                  <strong>
                    This installation can never be bound to again — by this customer or anybody
                    else.
                  </strong>{" "}
                  Its installation id is unique across the platform, so the application cannot
                  register its way back: the person will need a fresh install on a machine you have
                  not blocked. If you only want to end the session, use Sign out.
                </p>
              </>
            ),
            confirmLabel: "Block this device permanently",
            run: async (reason) => {
              const result = await revoke.mutateAsync({
                deviceID: device.id,
                reason,
                userID: userId,
              });
              onDone(revocationSummary(`${name} blocked`, result));
            },
          }}
        >
          Block device
        </ReasonButton>
      ) : null}
      {/* Offered only for a retire the CUSTOMER made. The server refuses
          anything else, so this condition decides what is rendered and never
          what is permitted — an operator cannot unblock a laptop by reaching
          the route directly. */}
      {mayRestore ? (
        <ReasonButton
          variant="secondary"
          busy={restore.isPending && restore.variables?.deviceID === device.id}
          mutation={{
            title: `Restore ${name}`,
            tone: "primary",
            description: (
              <>
                <p>
                  This customer retired this installation themselves — they can undo it from their
                  own account page, and so can you. Restoring makes the machine able to be signed
                  in on again with the app already installed on it;{" "}
                  <strong>nothing has to be reinstalled</strong>.
                </p>
                <p>
                  It does not sign anybody in. No session, binding or refresh token comes back —
                  the person opens the app on that machine and signs in, which is when the
                  installation is bound again.
                </p>
              </>
            ),
            confirmLabel: "Restore",
            run: async (reason) => {
              await restore.mutateAsync({
                deviceID: device.id,
                reason,
                userID: userId,
              });
              onDone(`${name} restored — the customer can sign in on it again`);
            },
          }}
        >
          Restore
        </ReasonButton>
      ) : null}
    </div>
  );
}

/**
 * A Tier-3 environment field, with "never reported" and "reported blank" kept
 * apart.
 *
 * The Admin API sends these as `string | null` with no omitempty, deliberately:
 * `null` means no client has ever told the platform this field, `""` means one
 * told it a blank. Collapsing them with `??` would throw away the difference
 * between "our client is old" and "our client sent nothing", which is the
 * first thing anyone asks when a device page looks empty. Two different
 * sentences, in two different styles, so they are distinguishable at a glance
 * and not only by reading.
 */
function Reported({
  value,
  mono = false,
  truncate = false,
}: {
  value: string | null | undefined;
  mono?: boolean;
  truncate?: boolean;
}) {
  if (value === null || value === undefined) {
    return <NotRecorded />;
  }
  if (value === "") {
    return (
      <span className="esm-blank-value" title="A client reported this field with an empty value">
        reported blank
      </span>
    );
  }
  if (!mono) {
    return <>{value}</>;
  }
  return (
    <code className={truncate ? "esm-long-value" : undefined} title={truncate ? value : undefined}>
      {value}
    </code>
  );
}

/** No client has ever reported this field. Its own component so the sentence
 *  is the same one everywhere — an operator comparing two devices must not
 *  have to work out whether "—" and "not recorded" mean the same thing. */
function NotRecorded() {
  return <span className="esm-not-recorded">not recorded</span>;
}

/**
 * The applications bound to this installation, as links DOWN to the bindings
 * table — which is where they can be revoked, and stays the one place that can.
 *
 * Revoked bindings are listed with their status rather than filtered out: this
 * installation appears on the page BECAUSE the account holds a binding on it,
 * live or dead, and "they used to have the app on that laptop" is the whole
 * answer to a compromise question.
 *
 * So an empty list here does not mean "nothing bound" — it cannot, or the
 * device would not be listed at all. It means the binding fell outside the
 * bindings table's own bound: the two lists are capped at 200 rows each,
 * independently, one ordered by last seen and the other by created.
 */
function DeviceBindings({ bindings }: { bindings: UserBinding[] }) {
  if (bindings.length === 0) {
    return (
      <span className="esm-muted-text">
        Not on this page. This account holds a binding on this installation — that is why it is
        listed — but the bindings table below is capped at its own 200 rows and this one is past
        them.
      </span>
    );
  }
  return (
    <ul className="esm-inline-list">
      {bindings.map((binding) => (
        <li key={binding.id}>
          <a href={`#${bindingAnchorID(binding.id)}`}>{binding.application_name}</a>{" "}
          <StatusBadge status={binding.status} />
        </li>
      ))}
    </ul>
  );
}

/** The installation's signing-key history, newest first.
 *
 *  Revoked keys are kept rather than filtered: "this device rotated its key an
 *  hour before the incident" is one of the few things this history can tell an
 *  operator, and it is invisible if only the live key is shown. */
function DeviceKeys({ keys }: { keys: DevicePublicKey[] }) {
  return (
    <details className="esm-device__history">
      <summary>
        Signing keys{" "}
        <span className="esm-muted-text">
          ({historyCount(keys.length, "key", MAX_DEVICE_KEYS)})
        </span>
      </summary>
      <DataTable<DevicePublicKey>
        rows={keys}
        rowKey={(row) => row.id}
        empty="No signing key has ever been registered for this installation."
        columns={[
          { key: "algorithm", header: "Algorithm", render: (row) => <code>{row.algorithm}</code> },
          {
            key: "fingerprint",
            header: "Fingerprint",
            // The same treatment every other id gets in a table: shortened to
            // scan, whole in the tooltip for the operator comparing it against
            // a value read off the machine.
            render: (row) => <ShortID value={row.fingerprint} />,
          },
          { key: "status", header: "Status", render: (row) => <StatusBadge status={row.status} /> },
          { key: "created", header: "Created", render: (row) => when(row.created_at) },
          {
            key: "revoked",
            header: "Revoked",
            render: (row) =>
              row.revoked_at ? (
                <>
                  <div>{when(row.revoked_at)}</div>
                  {/* Why, when the server recorded it: a key superseded by a
                      rotation and one retired with its device are the same
                      status and very different events. */}
                  {row.revoked_reason ? (
                    <span className="esm-muted-text">{row.revoked_reason}</span>
                  ) : null}
                </>
              ) : (
                <span className="esm-muted-text">—</span>
              ),
          },
        ]}
      />
    </details>
  );
}

/** The "was that you?" prompts raised for this INSTALLATION — for whoever was
 *  signing in on it, which is not necessarily the user whose page this is. */
function DeviceApprovals({ approvals }: { approvals: DeviceApproval[] }) {
  return (
    <details className="esm-device__history">
      <summary>
        Approval requests{" "}
        <span className="esm-muted-text">
          ({historyCount(approvals.length, "prompt", MAX_DEVICE_APPROVALS)})
        </span>
      </summary>
      <DataTable<DeviceApproval>
        rows={approvals}
        rowKey={(row) => row.id}
        empty="No approval prompt has ever been raised for this installation."
        columns={[
          {
            key: "application",
            header: "Application",
            render: (row) => (
              <>
                <div>{row.application_name}</div>
                <ShortID value={row.application_id} />
              </>
            ),
          },
          { key: "status", header: "Status", render: (row) => <StatusBadge status={row.status} /> },
          {
            key: "risk",
            header: "Risk",
            render: (row) => <Badge tone={riskTone(row.risk_level)}>{row.risk_level}</Badge>,
          },
          { key: "requested", header: "Raised", render: (row) => when(row.requested_at) },
          {
            key: "decided",
            header: "Decided",
            // Absent while pending and on one that expired unanswered — which
            // is the difference the status carries, so the em dash here is
            // read together with it rather than on its own.
            render: (row) => when(row.decided_at),
          },
          { key: "expires", header: "Expires", render: (row) => when(row.expires_at) },
        ]}
      />
    </details>
  );
}


/** What the Admin API loads per installation, bounded PER DEVICE server-side
 *  so one machine's rotations cannot crowd out another's history. The numbers
 *  are maxDeviceKeys and maxDeviceApprovals in internal/admin/directory.go; a
 *  full list here is a list that may have been cut, and the summary says so
 *  rather than presenting the twentieth key as the first one. */
const MAX_DEVICE_KEYS = 20;
const MAX_DEVICE_APPROVALS = 50;

/** "no key" / "1 key" / "3 keys", and at the server's bound, the fact that
 *  there may be more. The count belongs in the CLOSED disclosure: an empty
 *  history is a fact an operator should get without a click. */
function historyCount(count: number, noun: string, cap: number): string {
  if (count === 0) {
    return `no ${noun}`;
  }
  const counted = count === 1 ? `1 ${noun}` : `${count} ${noun}s`;
  return count < cap ? counted : `${counted} — the newest this page loads`;
}

/** Risk is not status, so it does not share statusTone: `high` is the row an
 *  operator is scanning for and `low` is the one they are not. */
function riskTone(risk: string): BadgeTone {
  switch (risk) {
    case "high":
      return "danger";
    case "normal":
      return "warning";
    default:
      return "neutral";
  }
}

/**
 * The anchor an event links to when it names a device.
 *
 * A hash, not a router navigation: the row is on THIS page, and pushing a new
 * history entry to move 200 pixels would make the browser's back button undo
 * a scroll instead of leaving the account.
 */
function deviceAnchorID(deviceID: string): string {
  return `device-${deviceID}`;
}

/** The other half of the same jump: a device names the applications bound to
 *  it and links DOWN to the binding row, which is the only place a binding can
 *  be revoked. Same reasoning as deviceAnchorID — both ends are on this page,
 *  so neither end is a navigation. */
function bindingAnchorID(bindingID: string): string {
  return `binding-${bindingID}`;
}

/** The permission the trail sits behind. `users:read` — the same one the
 *  account itself needs — because who signed in from where is part of who the
 *  account is, not a separate trust. See the route in internal/admin/http.go. */
const USERS = "users:read";

/** The three values auth.security_events.outcome's CHECK allows. Offered as a
 *  closed list because the server REFUSES an unknown one: an empty trail for
 *  `sucess` would read as "nothing happened to this account". */
const EVENT_OUTCOMES: SecurityEventOutcome[] = ["success", "failure", "denied"];

/** One step of "show more". Small on purpose: every read of this route writes
 *  an admin_actions row, so the page opens with a screenful rather than with
 *  the largest page the server would give it. */
const EVENT_PAGE = 25;

/** maxSecurityEventPage in internal/admin/directory.go. The server CLAMPS to
 *  it rather than refusing, so asking for more would silently return this
 *  many and the button would stop doing anything without saying so. */
const EVENT_PAGE_CEILING = 200;

interface EventFilterDraft {
  outcome: SecurityEventOutcome | "";
  eventType: string;
  since: string;
  until: string;
}

const NO_EVENT_FILTER: EventFilterDraft = { outcome: "", eventType: "", since: "", until: "" };

function eventFilterIsSet(draft: EventFilterDraft): boolean {
  return Boolean(draft.outcome || draft.eventType.trim() || draft.since || draft.until);
}

/**
 * What has happened to this account — sign-ins, refusals, registrations —
 * newest first.
 *
 * PAGED BY GROWING THE PAGE, not by stepping a cursor, and that is a decision
 * about correctness rather than about effort. The route offers `limit`,
 * `outcome`, `event_type` and a half-open `since`/`until` window and nothing
 * else; its order is `(occurred_at DESC, id DESC)` while `until` is exclusive
 * on the TIMESTAMP alone. So a "next page" that re-asked with
 * `until = the oldest row on screen` would silently drop every sibling
 * written at that same instant — and one sign-in writes several rows inside
 * one transaction at one `now()`, which is precisely the cluster an
 * investigation is reading. Asking for more of the same page cannot skip an
 * event; a cursor this API does not have would have to be faked to.
 *
 * The ceiling is the server's, and the card says so when it is reached rather
 * than leaving a "show more" that returns the same 200 rows for ever. Getting
 * past it is what the date window is for.
 *
 * Every read here writes an admin_actions row of its own with the filter
 * attached — reading somebody's authentication history is an act, and this
 * realm records acts. That is why the filter is applied on a button and not
 * on every keystroke.
 */
function SecurityEventsCard({
  userId,
  devices,
  operator,
}: {
  userId: string;
  devices: UserDevice[];
  operator: Operator;
}) {
  const mayRead = can(operator, USERS);
  const [draft, setDraft] = useState<EventFilterDraft>(NO_EVENT_FILTER);
  const [applied, setApplied] = useState<EventFilterDraft>(NO_EVENT_FILTER);
  const [limit, setLimit] = useState(EVENT_PAGE);

  const filter: SecurityEventFilter = {
    limit,
    outcome: applied.outcome || undefined,
    event_type: applied.eventType.trim() || undefined,
    since: toRFC3339(applied.since),
    until: toRFC3339(applied.until),
  };
  const events = useUserSecurityEvents(userId, filter, mayRead);

  if (!mayRead) {
    // Unreachable in practice — the account card above this one is behind the
    // same permission, so an operator without it never sees this far. Kept
    // because the alternative is a query fired to be refused, and the Guard
    // writes every refusal to the audit trail.
    return (
      <Card title="Events">
        <Alert tone="info" title="Not your permission">
          Reading an account's authentication trail needs users:read, which your roles do not
          grant.
        </Alert>
      </Card>
    );
  }
  if (events.error) {
    // A 403 can still arrive: the permission list this console holds is the
    // one from sign-in, and a role changed underneath an open page is exactly
    // the case where the server's answer is the true one.
    const forbidden = isApiError(events.error) && events.error.status === 403;
    return (
      <Card title="Events">
        <Alert
          tone={forbidden ? "info" : "danger"}
          title={forbidden ? "Not your permission" : "Could not load the events"}
        >
          {forbidden
            ? "Reading an account's authentication trail needs users:read, which your roles do not grant."
            : errorMessage(events.error)}
        </Alert>
      </Card>
    );
  }

  const rows = events.data?.security_events ?? [];
  // Only a device this page is already showing can be linked to. An event can
  // outlive the binding that put the installation in this list, and a link to
  // an anchor that is not on the page is a link that does nothing and does not
  // say why.
  const listed = new Set(devices.map((device) => device.id));
  const filtered = eventFilterIsSet(applied);
  const atCeiling = rows.length >= EVENT_PAGE_CEILING;

  return (
    <Card title="Events">
      <p className="esm-muted-text">
        The customer realm's authentication and device trail, newest first — sign-ins,
        registrations and the refusals in between. Reading it is recorded in the admin audit
        trail, with the filter below attached.
      </p>

      <form
        className="esm-filters"
        onSubmit={(event) => {
          event.preventDefault();
          setApplied(draft);
          // Back to one page. Keeping a 200-row limit across a filter change
          // would spend the largest read the server allows on a question the
          // operator has not looked at yet.
          setLimit(EVENT_PAGE);
        }}
      >
        <Field label="Outcome" htmlFor="events-outcome">
          <Select
            id="events-outcome"
            value={draft.outcome}
            onChange={(event) =>
              setDraft({
                ...draft,
                // The options are exactly EVENT_OUTCOMES plus the empty
                // string, so this narrows a <select>'s untyped value back to
                // what was rendered into it.
                outcome: event.target.value as SecurityEventOutcome | "",
              })
            }
          >
            <option value="">Any outcome</option>
            {EVENT_OUTCOMES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Event type"
          htmlFor="events-type"
          hint="An exact match — token.rejected, not token."
        >
          <Input
            id="events-type"
            value={draft.eventType}
            onChange={(event) => setDraft({ ...draft, eventType: event.target.value })}
          />
        </Field>
        {/* datetime-local, converted to a UTC instant before it is sent. From
            is inclusive and To is exclusive, so paging a day at a time never
            shows an event twice and never steps over one. */}
        <Field label="From" htmlFor="events-since">
          <Input
            id="events-since"
            type="datetime-local"
            value={draft.since}
            onChange={(event) => setDraft({ ...draft, since: event.target.value })}
          />
        </Field>
        <Field label="To" htmlFor="events-until" hint="Exclusive.">
          <Input
            id="events-until"
            type="datetime-local"
            value={draft.until}
            onChange={(event) => setDraft({ ...draft, until: event.target.value })}
          />
        </Field>
        <Button type="submit" busy={events.isFetching}>
          Apply
        </Button>
        {eventFilterIsSet(draft) || filtered ? (
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setDraft(NO_EVENT_FILTER);
              setApplied(NO_EVENT_FILTER);
              setLimit(EVENT_PAGE);
            }}
          >
            Clear
          </Button>
        ) : null}
      </form>

      {events.isPending ? (
        <Spinner label="Loading the events" />
      ) : (
        <>
          <DataTable<SecurityEvent>
            rows={rows}
            rowKey={(row) => row.id}
            empty={
              filtered
                ? "No event matches that filter. The window is half-open — From is included, To is not."
                : "No security event. Nothing has been recorded against this account: it has never signed in, registered a device or been refused one."
            }
            columns={[
              {
                key: "occurred",
                header: "When",
                // Milliseconds kept: one sign-in writes several rows at one
                // now(), and a second-precision render would show a refusal
                // and the refresh that caused it as simultaneous.
                render: (row) => whenExact(row.occurred_at),
              },
              {
                key: "event",
                header: "Event",
                render: (row) => <code>{row.event_type}</code>,
              },
              {
                key: "outcome",
                header: "Outcome",
                render: (row) => (
                  <Badge tone={eventOutcomeTone(row.outcome)}>{row.outcome}</Badge>
                ),
              },
              {
                key: "subject",
                header: "Subject",
                render: (row) => <EventSubject event={row} listed={listed} />,
              },
              {
                key: "from",
                header: "From",
                render: (row) => (
                  <>
                    <code>{row.ip || "—"}</code>
                    <div className="esm-muted-text">
                      <ShortID value={row.request_id} />
                    </div>
                    {row.user_agent ? (
                      <div className="esm-muted-text" title={row.user_agent}>
                        {row.user_agent.slice(0, 40)}
                        {row.user_agent.length > 40 ? "…" : ""}
                      </div>
                    ) : null}
                  </>
                ),
              },
              {
                key: "detail",
                header: "Detail",
                // The event's own payload, rendered as recorded. It is where
                // an investigation starts — the refusal reason on a rejected
                // token, the generation on a refresh — and its shape varies by
                // event_type, so there is nothing to lay out it into.
                render: (row) =>
                  row.detail && Object.keys(row.detail).length > 0 ? (
                    <code className="esm-short-id" title={JSON.stringify(row.detail)}>
                      {JSON.stringify(row.detail)}
                    </code>
                  ) : (
                    <span className="esm-muted-text">—</span>
                  ),
              },
            ]}
          />
          <EventPaging
            shown={rows.length}
            limit={limit}
            atCeiling={atCeiling}
            busy={events.isFetching}
            onMore={() => setLimit(Math.min(limit + EVENT_PAGE, EVENT_PAGE_CEILING))}
          />
        </>
      )}
    </Card>
  );
}

/**
 * Whatever the event names, and a way back to it.
 *
 * Every subject is optional because every subject column is nullable and
 * ON DELETE SET NULL: an OTP failure names no application, a registration
 * refusal names no device. The absence is rendered as an absence — a zero uuid
 * substituted for a subject that was never recorded is a fact the database
 * does not hold.
 */
function EventSubject({ event, listed }: { event: SecurityEvent; listed: Set<string> }) {
  const deviceLabel = event.device_name || event.installation_id || "device";
  const hasSubject = Boolean(event.application_id || event.device_id || event.session_id);

  if (!hasSubject) {
    return <span className="esm-muted-text">the account</span>;
  }
  return (
    <>
      {event.application_id ? (
        <div>{event.application_name || <ShortID value={event.application_id} />}</div>
      ) : null}
      {event.device_id ? (
        <div className="esm-muted-text">
          {listed.has(event.device_id) ? (
            // A same-page jump to the row above. The anchor is on the device
            // cell, so the browser lands on the machine this event is about.
            <a href={`#${deviceAnchorID(event.device_id)}`}>{deviceLabel}</a>
          ) : (
            <>
              {deviceLabel} <span title="This event names an installation that is not in the device table above — the account is no longer bound to it.">(not listed above)</span>
            </>
          )}
        </div>
      ) : null}
      {event.session_id ? (
        <div className="esm-muted-text">
          session <ShortID value={event.session_id} />
        </div>
      ) : null}
    </>
  );
}

/**
 * How much of the trail is on screen, and how to see more of it.
 *
 * A full page is not proof there is more — the trail may be exactly this long
 * — so the button never claims there is, and a short page states plainly that
 * this is all of it. At the server's ceiling the button is replaced by the
 * reason it is gone.
 */
function EventPaging({
  shown,
  limit,
  atCeiling,
  busy,
  onMore,
}: {
  shown: number;
  limit: number;
  atCeiling: boolean;
  busy: boolean;
  onMore: () => void;
}) {
  if (shown === 0) {
    return null;
  }
  if (atCeiling) {
    return (
      <Alert tone="info" title={`Showing the newest ${shown} events — this read's limit`}>
        The Admin API answers at most {EVENT_PAGE_CEILING} events per read, so there may be older
        ones this page cannot reach. Narrow it with the outcome or the event type, or move the To
        box back to the oldest time above and read the window before it.
      </Alert>
    );
  }
  if (shown < limit) {
    return (
      <p className="esm-muted-text">
        {shown} {shown === 1 ? "event" : "events"} — the whole trail for this filter.
      </p>
    );
  }
  return (
    <div className="esm-actions">
      <Button type="button" variant="secondary" busy={busy} onClick={onMore}>
        Show {EVENT_PAGE} more
      </Button>
      <span className="esm-muted-text">Showing the newest {shown}.</span>
    </div>
  );
}

/**
 * Outcome drives the colour, and `denied` is the loud one.
 *
 * Not statusTone: these three are not a lifecycle. A `failure` is somebody
 * mistyping a code, which is noise; a `denied` is the platform refusing an
 * account something it asked for, which is the row an operator scanning an
 * incident is looking for.
 */
function eventOutcomeTone(outcome: SecurityEventOutcome): BadgeTone {
  switch (outcome) {
    case "success":
      return "success";
    case "denied":
      return "danger";
    default:
      return "warning";
  }
}

/**
 * Who is on this installation, in the one cell an operator scans.
 *
 * Three distinct answers, and the third is the one worth the component: a
 * device listed on this page whose current occupant is SOMEBODY ELSE. The list
 * is "installations this user has been bound to", history included, so that row
 * is normal — and rendering it as a plain "signed in" badge would tell an
 * operator that the customer they are talking to is on a machine they left
 * months ago.
 */
function Occupancy({ device, accountBlocked }: { device: UserDevice; accountBlocked: boolean }) {
  if (!device.signed_in) {
    return <span className="esm-muted-text">nobody</span>;
  }
  if (!device.occupant_is_user) {
    return (
      <>
        <Badge tone="warning">another account</Badge>
        <div className="esm-muted-text">
          {device.occupant_email || device.occupant_user_id}
          {device.occupied_since ? <> · since {when(device.occupied_since)}</> : null}
        </div>
      </>
    );
  }
  return (
    <>
      {/* A suspended account's bindings are untouched but every request they
          carry is refused. "signed in" with no qualifier would contradict the
          account card directly above. */}
      <Badge tone={accountBlocked ? "neutral" : "success"}>
        {accountBlocked ? "signed in, blocked" : "this account"}
      </Badge>
      {device.occupied_since ? (
        <div className="esm-muted-text">since {when(device.occupied_since)}</div>
      ) : null}
    </>
  );
}

/**
 * What each installation is bound to, and the one place a binding is revoked.
 *
 * Kept as its own table below the devices rather than folded into them: a
 * binding is a fact about an APPLICATION on a machine, one account holds one
 * per application per device, and revoking one is a different decision from
 * anything the device card offers. The two are cross-linked instead — the
 * device lists its applications, each row here names its device — so the
 * question "which laptop is that" is one click either way.
 */
function BindingsCard({
  userId,
  bindings,
  devices,
  operator,
  onDone,
}: {
  userId: string;
  bindings: UserBinding[];
  devices: UserDevice[];
  operator: Operator;
  onDone: (message: string) => void;
}) {
  const revoke = useRevokeBinding();
  const mayRevoke = can(operator, REVOKE);
  // The device rows this page actually rendered. A binding whose device is not
  // among them gets no link: both cards are bounded at 200 rows server-side
  // and they are bounded independently, so a link to an absent anchor is a
  // click that silently does nothing.
  const listed = new Map(devices.map((device) => [device.id, device]));

  return (
    <Card title="Application bindings">
      <ErrorAlert error={revoke.error} title="That binding could not be revoked" />
      <DataTable<UserBinding>
        rows={bindings}
        rowKey={(row) => row.id}
        empty="No application is bound to a device on this account."
        columns={[
          {
            key: "application",
            header: "Application",
            // The anchor a device's binding list jumps to. On the cell rather
            // than the <tr> for the reason the device anchor is: DataTable is
            // shared with the other portals and owns the row element.
            render: (row) => (
              <div id={bindingAnchorID(row.id)} className="esm-anchor">
                {row.application_name}
              </div>
            ),
          },
          {
            key: "device",
            header: "Device",
            render: (row) => <BindingDevice binding={row} listed={listed} />,
          },
          { key: "status", header: "Status", render: (row) => <StatusBadge status={row.status} /> },
          { key: "approved", header: "Approved", render: (row) => when(row.approved_at) },
          { key: "used", header: "Last used", render: (row) => when(row.last_used_at) },
          { key: "revoked", header: "Revoked", render: (row) => when(row.revoked_at) },
          {
            key: "revoke",
            header: "",
            align: "end",
            render: (row) =>
              mayRevoke && row.status === "active" ? (
                <ReasonButton
                  variant="danger"
                  busy={revoke.isPending && revoke.variables?.bindingID === row.id}
                  mutation={{
                    title: `Revoke ${row.application_name} on this device`,
                    description: (
                      <p>
                        This signs {row.application_name} out of this one installation. The rest of
                        the account's devices are untouched.
                      </p>
                    ),
                    confirmLabel: "Revoke binding",
                    run: async (reason) => {
                      const result = await revoke.mutateAsync({
                        bindingID: row.id,
                        reason,
                        userID: userId,
                      });
                      onDone(revocationSummary(`${row.application_name} binding revoked`, result));
                    },
                  }}
                >
                  Revoke
                </ReasonButton>
              ) : null,
          },
        ]}
      />
    </Card>
  );
}

/**
 * The installation a binding sits on, named and linked.
 *
 * It used to be the raw device uuid, which is the one thing on this row an
 * operator cannot act on: it identifies the machine to the database and to
 * nobody else. The device's own name, linked UP to the section that holds its
 * environment, keys and buttons, answers "which laptop is that" without the
 * operator scrolling and matching thirty-six characters by eye. The uuid is
 * still here, shortened, because it is what the security trail and the
 * gateway logs key on.
 */
function BindingDevice({
  binding,
  listed,
}: {
  binding: UserBinding;
  listed: Map<string, UserDevice>;
}) {
  const device = listed.get(binding.device_id);
  if (!device) {
    return (
      <>
        <ShortID value={binding.device_id} />
        <div className="esm-muted-text">not listed above</div>
      </>
    );
  }
  return (
    <>
      <a href={`#${deviceAnchorID(device.id)}`}>{device.device_name || device.installation_id}</a>
      <div>
        <ShortID value={device.id} />
      </div>
    </>
  );
}

function SessionsCard({
  userId,
  sessions,
  operator,
  onDone,
}: {
  userId: string;
  sessions: UserAppSession[];
  operator: Operator;
  onDone: (message: string) => void;
}) {
  const revoke = useRevokeSession();
  // Sessions are ended with the same permission as the devices they run on —
  // every role that may suspend an account also holds devices:revoke, so this
  // hides nothing an operator could otherwise do. The server decides either
  // way, and records the refusal if it disagrees.
  const mayRevoke = can(operator, REVOKE);

  return (
    <Card title="Sessions">
      <ErrorAlert error={revoke.error} title="That session could not be ended" />
      <DataTable<UserAppSession>
        rows={sessions}
        rowKey={(row) => row.id}
        empty="Nothing is signed in on this account."
        columns={[
          { key: "application", header: "Application", render: (row) => row.application_name },
          {
            key: "device",
            // A session with no installation is a browser sign-in, and saying
            // so is more use than an empty cell.
            header: "Device",
            render: (row) => (row.device_id ? <code>{row.device_id}</code> : "Browser"),
          },
          { key: "scopes", header: "Scopes", render: (row) => <ScopeList scopes={row.scopes} /> },
          {
            key: "from",
            header: "From",
            render: (row) => (
              <>
                <div>{row.ip || "—"}</div>
                <span className="esm-muted-text">{row.user_agent ?? ""}</span>
              </>
            ),
          },
          { key: "started", header: "Started", render: (row) => when(row.created_at) },
          { key: "seen", header: "Last seen", render: (row) => when(row.last_seen_at) },
          {
            key: "state",
            header: "State",
            // A revoked session keeps its row and says so, rather than
            // vanishing: "did my revoke work" is the question this table
            // exists to answer.
            render: (row) =>
              row.revoked_at ? (
                <Badge tone="danger">revoked {when(row.revoked_at)}</Badge>
              ) : (
                <Badge tone="success">live until {when(row.expires_at)}</Badge>
              ),
          },
          {
            key: "revoke",
            header: "",
            align: "end",
            render: (row) =>
              mayRevoke && !row.revoked_at ? (
                <ReasonButton
                  variant="danger"
                  busy={revoke.isPending && revoke.variables?.sessionID === row.id}
                  mutation={{
                    title: `End the ${row.application_name} session`,
                    description: (
                      <p>
                        This ends one session and the refresh family behind it. The device stays
                        bound, so the customer can sign in again themselves.
                      </p>
                    ),
                    confirmLabel: "End session",
                    run: async (reason) => {
                      const result = await revoke.mutateAsync({
                        sessionID: row.id,
                        reason,
                        userID: userId,
                      });
                      onDone(revocationSummary(`${row.application_name} session ended`, result));
                    },
                  }}
                >
                  End session
                </ReasonButton>
              ) : null,
          },
        ]}
      />
    </Card>
  );
}

/**
 * Entitlements, read-only here.
 *
 * They are granted against an application, so they are revoked on the
 * application's page where the operator can see everyone else holding the
 * same grant. Shown here because "what is this account entitled to" is half
 * of every support conversation.
 */
function EntitlementsCard({ entitlements }: { entitlements: UserEntitlement[] }) {
  return (
    <Card title="Entitlements">
      <DataTable<UserEntitlement>
        rows={entitlements}
        rowKey={(row) => row.id}
        empty="This account is not entitled to any application."
        columns={[
          {
            key: "application",
            header: "Application",
            render: (row) => (
              <Link to="/applications/$applicationId" params={{ applicationId: row.application_id }}>
                {row.application_name}
              </Link>
            ),
          },
          { key: "scopes", header: "Scopes", render: (row) => <ScopeList scopes={row.scopes} /> },
          { key: "status", header: "Status", render: (row) => <StatusBadge status={row.status} /> },
          { key: "granted", header: "Granted", render: (row) => when(row.granted_at) },
        ]}
      />
    </Card>
  );
}
